/**
 * Tela exibida quando o schedule está vazio ou todos os slots estão fora do dayparting.
 * Mostra logo, nome do workspace, relógio e status de conectividade.
 */
import { useEffect, useRef, useState } from 'react'
import { env } from '@/env'
import { useAuthStore } from '@/store/auth.store'
import { usePlayerStore } from '@/store/player.store'
import { PLAYER_COLORS } from '@/utils/player-theme'

// ─── Rotating taglines ──────────────────────────────────────────────────────────

const TAGLINES = [
  'CONTEÚDO INTELIGENTE, IMPACTO REAL',
  'DOOH PROJETADO PARA RESULTADOS',
  'SUA MENSAGEM. LUGAR CERTO. HORA CERTA.',
  'CADA TELA, UMA OPORTUNIDADE',
  'DO BRIEFING À TELA EM MINUTOS',
  'DISTRIBUA CONTEÚDO. MEÇA IMPACTO.',
  'SIGNAGE QUE CONVERTE',
  'COMUNICAÇÃO VISUAL SEM FRICÇÃO'
]

function RotatingTagline() {
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const cycle = () => {
      setVisible(false)
      timerRef.current = setTimeout(() => {
        setIndex((i) => (i + 1) % TAGLINES.length)
        setVisible(true)
      }, 600)
    }
    const id = setInterval(cycle, 4000)
    return () => {
      clearInterval(id)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return (
    <div
      style={{
        fontSize: 13,
        fontFamily: 'monospace',
        letterSpacing: 5,
        textTransform: 'uppercase',
        color: PLAYER_COLORS.textMuted,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.5s ease',
        textAlign: 'center',
        maxWidth: 480,
        paddingTop: 24,
        lineHeight: 1.8
      }}
    >
      {TAGLINES[index]}
    </div>
  )
}

// ─── Inline corner node (neo-brutalist blueprint marker) ─────────────────────

function CornerNode({ pos }: { pos: 'tl' | 'tr' | 'bl' | 'br' }) {
  const size = 6
  const style: React.CSSProperties = {
    position: 'absolute',
    width: size,
    height: size,
    background: PLAYER_COLORS.brand,
    zIndex: 10,
    pointerEvents: 'none',
    ...(pos === 'tl' && { top: -size / 2, left: -size / 2 }),
    ...(pos === 'tr' && { top: -size / 2, right: -size / 2 }),
    ...(pos === 'bl' && { bottom: -size / 2, left: -size / 2 }),
    ...(pos === 'br' && { bottom: -size / 2, right: -size / 2 })
  }
  return <span style={style} />
}

// ─── Inlined SVG logo (paths from logo-sketch.tsx) ───────────────────────────

function PlayerLogoSketch({ opacity = 0.5 }: { opacity?: number }) {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: decorative logo
    <svg
      viewBox="0 0 600 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', maxWidth: 560, opacity }}
    >
      <defs>
        <pattern id="fp-grid" width={6} height={6} patternUnits="userSpaceOnUse">
          <path d="M10 0H0V10" fill="none" stroke={PLAYER_COLORS.textPrimary} strokeWidth={0.4} opacity={0.3} />
        </pattern>
      </defs>
      <path
        d="M496.707 45.5C511.281 45.5 523.897 50.9433 534.534 61.8027C545.171 72.5516 550.5 85.2784 550.5 99.959C550.5 112.614 546.689 123.88 539.071 133.739L536.278 137.308L545.772 154.004L546.197 154.751H525.713L522.449 148.387L516.552 150.714C510.164 153.405 503.547 154.751 496.707 154.751C482.148 154.751 469.64 149.588 459.209 139.276L458.712 138.779C448.184 128.027 442.914 115.078 442.914 99.959C442.914 84.9501 448.185 72.1112 458.714 61.4697C469.243 50.8287 481.917 45.5 496.707 45.5ZM496.707 63.6533C486.851 63.6533 478.429 67.2065 471.416 74.3291C464.402 81.4526 460.902 89.9872 460.902 99.959C460.902 110.044 464.404 118.689 471.419 125.924C478.433 133.157 486.853 136.764 496.707 136.764C506.336 136.764 514.703 133.16 521.831 125.922C528.959 118.574 532.513 109.928 532.513 99.959C532.513 90.1024 528.959 81.6242 521.834 74.499L521.828 74.4932C514.813 67.2592 506.448 63.6534 496.707 63.6533Z"
        stroke={PLAYER_COLORS.brand}
        strokeWidth={0.8}
      />
      <path d="M424.437 45.5V154.751H406.45V45.5H424.437Z" stroke={PLAYER_COLORS.brand} strokeWidth={0.8} />
      <path
        d="M392.082 45.5V63.4873H359.108V154.751H341.12V63.4873H308.479V45.5H392.082Z"
        stroke={PLAYER_COLORS.brand}
        strokeWidth={0.8}
      />
      <path
        d="M249.287 76.6436C249.287 78.7762 249.82 80.682 250.877 82.374C251.962 84.1102 253.089 85.4468 254.251 86.4014C255.535 87.2536 257.315 88.1193 259.607 88.9932C261.916 89.7628 263.671 90.3115 264.877 90.6416C266.213 90.8646 268.043 91.1984 270.365 91.6406C273.448 92.1912 275.245 92.5271 275.717 92.6445C285.447 94.9932 292.827 99.0884 297.799 104.965C302.779 110.85 304.808 117.98 303.91 126.312C303.234 134.448 299.611 141.232 293.073 146.643C286.538 152.051 278.762 154.751 269.772 154.751C260.67 154.751 251.696 151.941 242.858 146.347L252.391 133.292C258.509 136.013 263.815 137.43 268.939 137.43C273.509 137.43 277.575 136.235 281.152 133.851C284.669 131.396 286.366 128.005 286.259 123.62C286.259 121.474 285.727 119.63 284.677 118.056C283.682 116.289 282.615 115.02 281.468 114.187C280.387 113.217 278.791 112.355 276.601 111.589C274.489 110.811 272.909 110.323 271.838 110.108C270.738 109.888 269.136 109.612 267.029 109.279C263.447 108.608 259.88 107.772 256.313 106.769L253.792 105.929C246.154 103.121 240.292 98.9602 236.227 93.4268C232.261 87.7607 230.68 81.3096 231.47 74.0908C232.15 65.8535 235.77 59.0179 242.307 53.6084C248.843 48.1992 256.675 45.5 265.775 45.5C274.435 45.5 283.406 48.3123 292.679 53.8984L283.158 67.123C277.039 64.4047 271.677 62.9873 266.441 62.9873C261.875 62.9873 257.813 64.2361 254.237 66.7285C250.825 69.0728 249.18 72.3526 249.287 76.6309V76.6436Z"
        stroke={PLAYER_COLORS.brand}
        strokeWidth={0.8}
      />
      <path
        d="M183.475 45.8086L228.441 154.06L228.729 154.751H210.826L183.013 88.4287L155.329 154.444L155.2 154.751H137.131L182.552 45.8076L183.014 44.6982L183.475 45.8086Z"
        stroke={PLAYER_COLORS.brand}
        strokeWidth={0.8}
      />
      <path
        d="M102.294 45.5C112.827 45.5 122.415 48.3596 131.048 54.0771L119.657 67.9795C114.112 65.1321 108.54 63.6534 102.294 63.6533C92.6635 63.6533 84.4663 67.2041 77.6748 74.3242C70.8811 81.4466 67.4873 89.9822 67.4873 99.959C67.4873 110.16 70.8817 118.862 77.6738 126.094C84.4663 133.214 92.6636 136.764 102.294 136.764C108.314 136.764 113.89 135.287 119.031 132.334L131.146 145.426C122.416 151.892 112.827 154.751 102.294 154.751C87.7225 154.751 75.2697 149.42 64.9629 138.777C54.6568 128.135 49.5 115.297 49.5 100.292C49.5 85.1766 54.6561 72.2826 64.9629 61.6396C75.2688 50.8867 87.7217 45.5 102.294 45.5Z"
        stroke={PLAYER_COLORS.brand}
        strokeWidth={0.8}
      />
    </svg>
  )
}

export function FallbackScreen() {
  const creds = useAuthStore((s) => s.credentials)
  const isOffline = usePlayerStore((s) => s.isOffline)

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: PLAYER_COLORS.bgRoot,
        display: 'flex',
        flexDirection: 'row',
        fontFamily: 'monospace'
      }}
    >
      {/* ── Left panel ── */}
      <div
        style={{
          position: 'relative',
          width: '40%',
          flexShrink: 0,
          background: PLAYER_COLORS.bgDark,
          borderRight: `1px solid ${PLAYER_COLORS.brand}`,
          display: 'flex',
          flexDirection: 'column',
          padding: '40px 44px'
        }}
      >
        <CornerNode pos="tl" />
        <CornerNode pos="tr" />
        <CornerNode pos="bl" />
        <CornerNode pos="br" />

        {/* Blueprint sketch overlay — no viewBox, percentage coords, tiles correctly at any resolution */}
        <svg
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            overflow: 'hidden'
          }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Repeating grid cell */}
            <pattern id="bp-cell" width={80} height={80} patternUnits="userSpaceOnUse">
              <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#1e1e1e" strokeWidth={0.5} />
            </pattern>
          </defs>

          {/* ── Tiled grid ── */}
          <rect width="100%" height="100%" fill="url(#bp-cell)" />

          {/* ── Top dimension bracket ── */}
          <line x1="11%" y1="4.5%" x2="89%" y2="4.5%" stroke="#272727" strokeWidth={0.6} />
          <line x1="11%" y1="3%" x2="11%" y2="6%" stroke="#272727" strokeWidth={0.6} />
          <line x1="89%" y1="3%" x2="89%" y2="6%" stroke="#272727" strokeWidth={0.6} />
          <text x="50%" y="4%" textAnchor="middle" fill="#303030" fontSize={9} fontFamily="monospace" letterSpacing={2}>
            W: 40vw
          </text>

          {/* ── Left side dimension bracket ── */}
          <line x1="4.5%" y1="6%" x2="4.5%" y2="94%" stroke="#272727" strokeWidth={0.6} />
          <line x1="3%" y1="6%" x2="6%" y2="6%" stroke="#272727" strokeWidth={0.6} />
          <line x1="3%" y1="94%" x2="6%" y2="94%" stroke="#272727" strokeWidth={0.6} />
          <text
            x="3.5%"
            y="50%"
            textAnchor="middle"
            fill="#303030"
            fontSize={9}
            fontFamily="monospace"
            letterSpacing={2}
            transform="rotate(-90)"
            style={{ transformOrigin: '3.5% 50%' }}
          >
            H: 100vh
          </text>

          {/* ── Corner registration marks (L-shaped, always perpendicular) ── */}
          <line x1="0" y1="8%" x2="6%" y2="8%" stroke="#2a2a2a" strokeWidth={0.6} strokeDasharray="3 3" />
          <line x1="6%" y1="0" x2="6%" y2="8%" stroke="#2a2a2a" strokeWidth={0.6} strokeDasharray="3 3" />

          <line x1="94%" y1="0" x2="94%" y2="8%" stroke="#2a2a2a" strokeWidth={0.6} strokeDasharray="3 3" />
          <line x1="94%" y1="8%" x2="100%" y2="8%" stroke="#2a2a2a" strokeWidth={0.6} strokeDasharray="3 3" />

          <line x1="0" y1="92%" x2="6%" y2="92%" stroke="#2a2a2a" strokeWidth={0.6} strokeDasharray="3 3" />
          <line x1="6%" y1="92%" x2="6%" y2="100%" stroke="#2a2a2a" strokeWidth={0.6} strokeDasharray="3 3" />

          <line x1="94%" y1="92%" x2="100%" y2="92%" stroke="#2a2a2a" strokeWidth={0.6} strokeDasharray="3 3" />
          <line x1="94%" y1="92%" x2="94%" y2="100%" stroke="#2a2a2a" strokeWidth={0.6} strokeDasharray="3 3" />

          {/* ── Crosshair top-right ── */}
          <circle cx="83%" cy="14%" r={18} stroke="#f5c518" strokeWidth={0.5} strokeOpacity={0.12} fill="none" />
          <circle cx="83%" cy="14%" r={3.5} stroke="#f5c518" strokeWidth={0.5} strokeOpacity={0.14} fill="none" />
          <line x1="78%" y1="14%" x2="88%" y2="14%" stroke="#f5c518" strokeWidth={0.4} strokeOpacity={0.14} />
          <line x1="83%" y1="9%" x2="83%" y2="19%" stroke="#f5c518" strokeWidth={0.4} strokeOpacity={0.14} />
          <text x="89%" y="13%" fill="#333" fontSize={8} fontFamily="monospace">
            {'// 001'}
          </text>
          <text x="89%" y="15.5%" fill="#333" fontSize={8} fontFamily="monospace">
            layer_A
          </text>

          {/* ── Crosshair bottom-left ── */}
          <circle cx="17%" cy="84%" r={18} stroke="#f5c518" strokeWidth={0.5} strokeOpacity={0.1} fill="none" />
          <circle cx="17%" cy="84%" r={3.5} stroke="#f5c518" strokeWidth={0.5} strokeOpacity={0.12} fill="none" />
          <line x1="12%" y1="84%" x2="22%" y2="84%" stroke="#f5c518" strokeWidth={0.4} strokeOpacity={0.12} />
          <line x1="17%" y1="79%" x2="17%" y2="89%" stroke="#f5c518" strokeWidth={0.4} strokeOpacity={0.12} />
          <text x="23%" y="83%" fill="#333" fontSize={8} fontFamily="monospace">
            {'// 002'}
          </text>
          <text x="23%" y="85.5%" fill="#333" fontSize={8} fontFamily="monospace">
            layer_B
          </text>

          {/* ── Content bounding box ── */}
          <rect
            x="10%"
            y="28%"
            width="80%"
            height="38%"
            stroke="#282828"
            strokeWidth={0.5}
            strokeDasharray="7 5"
            fill="none"
          />
          <text x="10%" y="27%" fill="#2e2e2e" fontSize={8} fontFamily="monospace" letterSpacing={1}>
            content_block
          </text>
          {/* Corner ticks on bounding box */}
          <line x1="10%" y1="28%" x2="14%" y2="28%" stroke="#303030" strokeWidth={0.6} />
          <line x1="10%" y1="28%" x2="10%" y2="31%" stroke="#303030" strokeWidth={0.6} />
          <line x1="90%" y1="66%" x2="86%" y2="66%" stroke="#303030" strokeWidth={0.6} />
          <line x1="90%" y1="66%" x2="90%" y2="63%" stroke="#303030" strokeWidth={0.6} />

          {/* ── Footer annotation line ── */}
          <line x1="10%" y1="93%" x2="90%" y2="93%" stroke="#232323" strokeWidth={0.5} strokeDasharray="2 7" />
          <text x="10%" y="95.5%" fill="#2c2c2c" fontSize={8} fontFamily="monospace" letterSpacing={1}>
            footer / rev.
          </text>

          {/* ── Angled annotation (bottom-right area) ── */}
          <line x1="74%" y1="78%" x2="88%" y2="87%" stroke="#282828" strokeWidth={0.5} strokeDasharray="3 3" />
          <circle cx="74%" cy="78%" r={2} fill="#303030" />
          <text x="89%" y="88.5%" fill="#2e2e2e" fontSize={8} fontFamily="monospace">
            ∅ node_02
          </text>
        </svg>

        {/* Main headline */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0 }}>
          <div
            style={{
              fontSize: 'clamp(32px, 3.5vw, 52px)',
              fontWeight: 700,
              color: PLAYER_COLORS.textPrimary,
              textTransform: 'uppercase',
              letterSpacing: 2,
              lineHeight: 1.1
            }}
          >
            <span style={{ color: PLAYER_COLORS.brand }}>SEM CONTEÚDO</span>
            <br />
            ATRIBUÍDO
            <br />
            AO PLAYER
          </div>

          <div
            style={{
              marginTop: 24,
              color: PLAYER_COLORS.textMuted,
              fontSize: 13,
              lineHeight: 1.7,
              maxWidth: 340
            }}
          >
            Este player está registrado com sucesso, mas nenhum conteúdo foi atribuído ainda.
          </div>

          {/* Workspace badge */}
          {creds?.workspaceName && (
            <div style={{ marginTop: 32 }}>
              <span
                style={{
                  display: 'inline-block',
                  border: `1px solid ${PLAYER_COLORS.borderSubtle}`,
                  padding: '6px 14px',
                  color: PLAYER_COLORS.textMuted,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 3
                }}
              >
                {creds.workspaceName}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: `1px solid ${PLAYER_COLORS.border}`,
            paddingTop: 16,
            marginTop: 24
          }}
        >
          <span style={{ color: PLAYER_COLORS.textFaint, fontSize: 11, letterSpacing: 2 }}>app.castiq.com</span>
          <span style={{ color: PLAYER_COLORS.textFaint, fontSize: 11, fontFamily: 'monospace', letterSpacing: 1 }}>
            v{env.VITE_PLAYER_RELEASE}
          </span>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          background: PLAYER_COLORS.bgDark,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {/* Dot-grid background */}
        <svg
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="rp-dots" width={24} height={24} patternUnits="userSpaceOnUse">
              <circle cx={1} cy={1} r={0.8} fill={PLAYER_COLORS.borderSubtle} />
            </pattern>
            <linearGradient id="rp-fade" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#000" stopOpacity={1} />
              <stop offset="40%" stopColor="#000" stopOpacity={0} />
            </linearGradient>
            <mask id="rp-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect width="100%" height="100%" fill="url(#rp-fade)" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="url(#rp-dots)" mask="url(#rp-mask)" />
        </svg>

        {/* Logo + tagline — centered column */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '80%',
            maxWidth: 520
          }}
        >
          <PlayerLogoSketch opacity={0.55} />
          <RotatingTagline />
        </div>

        {/* Offline badge */}
        {isOffline && (
          <div
            style={{
              position: 'absolute',
              bottom: 24,
              right: 24,
              background: PLAYER_COLORS.bgCardAlt,
              border: `1px solid ${PLAYER_COLORS.borderSubtle}`,
              padding: '6px 12px',
              color: PLAYER_COLORS.textWarning,
              fontSize: 11,
              fontFamily: 'monospace',
              letterSpacing: 2,
              textTransform: 'uppercase'
            }}
          >
            ● sem conexão
          </div>
        )}
      </div>
    </div>
  )
}
